import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import Autocomplete from "@mui/material/Autocomplete";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import { Trash2 } from "lucide-react";
import { Alert, Button, Spinner, Table } from "react-bootstrap";
import {
    TECH_CARD_AUTOCOMPLETE_COMPONENTS_PROPS,
    TECH_CARD_AUTOCOMPLETE_LISTBOX_SX,
    TECH_CARD_MUI_FIELD_SX,
} from "../helpers/muiTechCardEditorFieldSx";

const API = "/api/stock-bags";

let draftKeySeq = 0;
const emptyDraft = () => ({ key: `draft-${++draftKeySeq}`, quantity: "1" });

function formatBagsAxiosError(e, fallback = "Помилка запиту") {
    const d = e?.response?.data;
    return (typeof d?.error === "string" && d.error.trim()) || e?.message || fallback;
}

/** «Худі петля 2023 / Молочний / M» */
function skuOptionLabel(p) {
    const props = (p.properties || [])
        .map((x) => String(x?.value ?? "").trim())
        .filter(Boolean);
    return [String(p.name || "").trim(), ...props].filter(Boolean).join(" / ");
}

const DELETE_ICON_BUTTON_SX = {
    width: 36,
    height: 36,
    padding: 0.5,
    color: "text.secondary",
    "&:hover": {
        color: "error.main",
        backgroundColor: "action.hover",
    },
};

function BagItemRow({ rowIndex, item, options, optionById, onPickSku, onQuantityCommit, onRemove }) {
    const [quantity, setQuantity] = useState(String(item.quantity ?? "1"));

    useEffect(() => {
        setQuantity(String(item.quantity ?? "1"));
    }, [item.quantity]);

    const value =
        item.sku_id != null
            ? optionById.get(Number(item.sku_id)) || {
                  id: Number(item.sku_id),
                  label: item.text,
                  sku: "",
                  haystack: "",
              }
            : null;

    return (
        <tr>
            <td className="text-center text-muted small py-1">{rowIndex + 1}</td>
            <td className="py-1 text-start">
                <Autocomplete
                    size="small"
                    sx={TECH_CARD_MUI_FIELD_SX}
                    componentsProps={TECH_CARD_AUTOCOMPLETE_COMPONENTS_PROPS}
                    ListboxProps={{ sx: TECH_CARD_AUTOCOMPLETE_LISTBOX_SX }}
                    options={options}
                    getOptionLabel={(o) => o.label}
                    renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                            <div className="d-flex flex-column lh-sm">
                                <span>{option.label}</span>
                                {option.sku ? (
                                    <span
                                        className="text-muted pt-1"
                                        style={{ fontSize: "0.75rem", lineHeight: 1.2 }}
                                    >
                                        #{option.sku}
                                    </span>
                                ) : null}
                            </div>
                        </li>
                    )}
                    value={value}
                    onChange={(_, newVal) => {
                        if (newVal) onPickSku(newVal, quantity);
                    }}
                    disableClearable={value != null}
                    isOptionEqualToValue={(a, b) => a?.id === b?.id}
                    filterOptions={(opts, state) => {
                        const terms = state.inputValue.trim().toLowerCase().split(/\s+/).filter(Boolean);
                        if (!terms.length) return opts.slice(0, 200);
                        return opts
                            .filter((o) => terms.every((t) => o.haystack.includes(t)))
                            .slice(0, 200);
                    }}
                    noOptionsText="Нічого не знайдено"
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            hiddenLabel
                            placeholder="Назва, колір, розмір, SKU…"
                            size="small"
                            InputProps={{ ...params.InputProps, notched: false }}
                        />
                    )}
                />
            </td>
            <td className="py-1 tech-card-td-qty">
                <TextField
                    size="small"
                    fullWidth
                    hiddenLabel
                    InputProps={{ notched: false }}
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    onBlur={() => {
                        const n = Number(String(quantity).replace(",", "."));
                        if (!Number.isFinite(n) || n < 0) {
                            setQuantity(String(item.quantity ?? "1"));
                            return;
                        }
                        if (n !== Number(item.quantity)) onQuantityCommit(n);
                    }}
                    inputProps={{ inputMode: "decimal", "aria-label": "Кількість" }}
                    sx={TECH_CARD_MUI_FIELD_SX}
                />
            </td>
            <td className="py-1 align-middle tech-card-td-actions">
                <IconButton
                    type="button"
                    size="small"
                    title="Прибрати річ з мішка"
                    aria-label="Прибрати річ з мішка"
                    onClick={onRemove}
                    sx={DELETE_ICON_BUTTON_SX}
                >
                    <DeleteOutline fontSize="small" />
                </IconButton>
            </td>
        </tr>
    );
}

/**
 * Мішки на складі: список мішків, у кожному — таблиця речей (як рядки матеріалів у техкарті).
 * @param {{ products: object[] }} props products — варіанти з `/api/stocks` (ProductW)
 */
export default function StockBagsPanel({ products }) {
    const [bags, setBags] = useState([]);
    const [draftsByBag, setDraftsByBag] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [addingBag, setAddingBag] = useState(false);
    /** Пошук речі по мішках: лишає тільки мішки й рядки, що збігаються. */
    const [search, setSearch] = useState("");
    /** Автосписання за відправленими замовленнями CRM (статус «В дорозі»). */
    const [deductions, setDeductions] = useState([]);

    const options = useMemo(() => {
        const list = (products || [])
            .filter((p) => p?.id != null)
            .map((p) => {
                const label = skuOptionLabel(p);
                return {
                    id: Number(p.id),
                    label,
                    sku: p.sku || "",
                    haystack: `${label} ${p.sku || ""}`.toLowerCase(),
                };
            });
        list.sort((a, b) => a.label.localeCompare(b.label, "uk", { numeric: true }));
        return list;
    }, [products]);

    const optionById = useMemo(() => new Map(options.map((o) => [o.id, o])), [options]);

    const load = useCallback(async () => {
        try {
            const res = await axios.get(API);
            setBags(Array.isArray(res.data) ? res.data : []);
            setError(null);
            axios
                .get(`${API}/deductions`)
                .then((r) => setDeductions(Array.isArray(r.data) ? r.data : []))
                .catch(() => setDeductions([]));
        } catch (e) {
            setError(formatBagsAxiosError(e, "Не вдалося завантажити мішки"));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void load();
    }, [load]);

    /** Запит + оновлення стану; при помилці перечитуємо список із сервера. */
    const run = async (request, applyResult) => {
        try {
            const res = await request();
            setError(null);
            applyResult(res.data);
        } catch (e) {
            setError(formatBagsAxiosError(e));
            void load();
        }
    };

    const patchBagItems = (bagId, fn) =>
        setBags((prev) =>
            prev.map((b) => (String(b._id) === String(bagId) ? { ...b, items: fn(b.items || []) } : b))
        );

    const removeDraft = (bagId, key) =>
        setDraftsByBag((prev) => ({
            ...prev,
            [bagId]: (prev[bagId] || []).filter((d) => d.key !== key),
        }));

    const addBag = async () => {
        setAddingBag(true);
        await run(
            () => axios.post(API, {}),
            (bag) => setBags((prev) => [...prev, bag])
        );
        setAddingBag(false);
    };

    const deleteBag = (bag) => {
        const count = (bag.items || []).length;
        const msg = count
            ? `Видалити «${bag.name}» разом із речами (${count})?`
            : `Видалити «${bag.name}»?`;
        if (!window.confirm(msg)) return;
        void run(
            () => axios.delete(`${API}/${bag._id}`),
            () => setBags((prev) => prev.filter((b) => String(b._id) !== String(bag._id)))
        );
    };

    const createItemFromDraft = (bagId, draftKey, option, quantityRaw) => {
        const n = Number(String(quantityRaw).replace(",", "."));
        const quantity = Number.isFinite(n) && n >= 0 ? n : 1;
        void run(
            () =>
                axios.post(`${API}/${bagId}/items`, {
                    sku_id: option.id,
                    text: option.label,
                    quantity,
                }),
            (item) => {
                patchBagItems(bagId, (items) => [...items, item]);
                removeDraft(bagId, draftKey);
            }
        );
    };

    const updateItem = (bagId, itemId, body) =>
        void run(
            () => axios.patch(`${API}/${bagId}/items/${itemId}`, body),
            (item) =>
                patchBagItems(bagId, (items) =>
                    items.map((it) => (String(it._id) === String(itemId) ? item : it))
                )
        );

    const deleteItem = (bagId, itemId) =>
        void run(
            () => axios.delete(`${API}/${bagId}/items/${itemId}`),
            () => patchBagItems(bagId, (items) => items.filter((it) => String(it._id) !== String(itemId)))
        );

    const searchTerms = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const searching = searchTerms.length > 0;
    const itemMatchesSearch = (item) => {
        const haystack = `${item.text || ""} ${optionById.get(Number(item.sku_id))?.haystack || ""}`.toLowerCase();
        return searchTerms.every((t) => haystack.includes(t));
    };
    const visibleBags = searching
        ? bags
              .map((bag) => ({ ...bag, items: (bag.items || []).filter(itemMatchesSearch) }))
              .filter((bag) => bag.items.length > 0)
        : bags;

    return (
        <div className="stocks-page__bags-panel">
            <h4 className="stocks-page__bags-title">Мішки ({bags.length})</h4>

            {error ? (
                <Alert variant="danger" className="py-2" onClose={() => setError(null)} dismissible>
                    {error}
                </Alert>
            ) : null}

            {loading ? (
                <div className="text-center py-3">
                    <Spinner animation="border" role="status" size="sm" />
                </div>
            ) : (
                <>
                    {bags.length === 0 ? <p className="text-muted">Мішків ще немає.</p> : null}

                    {bags.length > 0 ? (
                        <TextField
                            size="small"
                            fullWidth
                            hiddenLabel
                            type="search"
                            className="stocks-page__bags-search"
                            placeholder="Пошук речі: назва, колір, розмір, SKU…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            InputProps={{ notched: false }}
                            inputProps={{ "aria-label": "Пошук речі по мішках" }}
                            sx={TECH_CARD_MUI_FIELD_SX}
                        />
                    ) : null}

                    {searching && visibleBags.length === 0 ? (
                        <p className="text-muted">У мішках нічого не знайдено.</p>
                    ) : null}

                    {visibleBags.map((bag) => {
                        const items = bag.items || [];
                        /* Під час пошуку показуємо лише знайдені рядки, без чернеток */
                        const drafts = searching ? [] : draftsByBag[bag._id] || [];
                        const totalQty = items.reduce((sum, it) => sum + (Number(it.quantity) || 0), 0);
                        return (
                            <section key={bag._id} className="stocks-page__bag">
                                <div className="stocks-page__bag-header">
                                    <h5 className="stocks-page__bag-name">{bag.name}</h5>
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="outline-danger"
                                        title="Видалити мішок"
                                        aria-label={`Видалити ${bag.name}`}
                                        onClick={() => deleteBag(bag)}
                                    >
                                        <Trash2 size={16} />
                                    </Button>
                                </div>

                                <div className="table-responsive border rounded mb-2">
                                    <Table
                                        bordered
                                        hover
                                        size="sm"
                                        className="align-middle mb-0 tech-card-materials-editor-table"
                                    >
                                        <thead className="table-light">
                                            <tr>
                                                <th className="text-center text-nowrap" style={{ width: 40 }}>
                                                    #
                                                </th>
                                                <th className="text-start">Річ</th>
                                                <th className="text-nowrap tech-card-th-qty">Кількість</th>
                                                <th className="text-nowrap tech-card-th-actions">Дії</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {items.length + drafts.length === 0 ? (
                                                <tr>
                                                    <td colSpan={4} className="text-center text-muted small py-2">
                                                        Мішок порожній — натисніть «+», щоб додати річ
                                                    </td>
                                                </tr>
                                            ) : null}
                                            {items.map((item, i) => (
                                                <BagItemRow
                                                    key={item._id}
                                                    rowIndex={i}
                                                    item={item}
                                                    options={options}
                                                    optionById={optionById}
                                                    onPickSku={(option) => {
                                                        if (option.id === Number(item.sku_id)) return;
                                                        updateItem(bag._id, item._id, {
                                                            sku_id: option.id,
                                                            text: option.label,
                                                        });
                                                    }}
                                                    onQuantityCommit={(quantity) =>
                                                        updateItem(bag._id, item._id, { quantity })
                                                    }
                                                    onRemove={() => deleteItem(bag._id, item._id)}
                                                />
                                            ))}
                                            {drafts.map((draft, i) => (
                                                <BagItemRow
                                                    key={draft.key}
                                                    rowIndex={items.length + i}
                                                    item={draft}
                                                    options={options}
                                                    optionById={optionById}
                                                    onPickSku={(option, quantity) =>
                                                        createItemFromDraft(bag._id, draft.key, option, quantity)
                                                    }
                                                    onQuantityCommit={(quantity) =>
                                                        setDraftsByBag((prev) => ({
                                                            ...prev,
                                                            [bag._id]: (prev[bag._id] || []).map((d) =>
                                                                d.key === draft.key
                                                                    ? { ...d, quantity: String(quantity) }
                                                                    : d
                                                            ),
                                                        }))
                                                    }
                                                    onRemove={() => removeDraft(bag._id, draft.key)}
                                                />
                                            ))}
                                        </tbody>
                                        <tfoot className="table-light">
                                            <tr>
                                                <td colSpan={2} className="text-end fw-semibold small py-2">
                                                    {searching ? "Знайдено в мішку" : "Усього речей у мішку"}
                                                </td>
                                                <td className="fw-semibold small py-2 text-nowrap">
                                                    {totalQty} шт.
                                                </td>
                                                <td className="py-2" aria-hidden="true" />
                                            </tr>
                                        </tfoot>
                                    </Table>
                                </div>
                                {searching ? null : (
                                <Button
                                    type="button"
                                    variant="outline-primary"
                                    className="px-3"
                                    onClick={() =>
                                        setDraftsByBag((prev) => ({
                                            ...prev,
                                            [bag._id]: [...(prev[bag._id] || []), emptyDraft()],
                                        }))
                                    }
                                    title="Додати річ у мішок"
                                    aria-label={`Додати річ у ${bag.name}`}
                                >
                                    +
                                </Button>
                                )}
                            </section>
                        );
                    })}

                    <Button type="button" variant="dark" onClick={addBag} disabled={addingBag}>
                        {addingBag ? "Додаємо…" : "+ Додати мішок"}
                    </Button>

                    {deductions.length ? (
                        <div className="stocks-page__bag-deductions">
                            <h6 className="mb-2">Останні автосписання (замовлення «В дорозі»)</h6>
                            <ul className="list-unstyled small mb-0">
                                {deductions.map((d) => (
                                    <li key={d._id} className="mb-1">
                                        <span className="fw-semibold">№{d.orderId}</span>{" "}
                                        <span className="text-muted">
                                            {new Date(d.createdAt).toLocaleString("uk-UA", {
                                                day: "2-digit",
                                                month: "2-digit",
                                                hour: "2-digit",
                                                minute: "2-digit",
                                            })}
                                        </span>
                                        {(d.lines || []).map((l, i) => (
                                            <div key={`l-${i}`}>
                                                −{l.quantity} · {l.text} · {l.bagName}
                                            </div>
                                        ))}
                                        {(d.missing || []).map((m, i) => (
                                            <div key={`m-${i}`} className="text-danger">
                                                не знайдено в мішках: {m.text} × {m.quantity}
                                            </div>
                                        ))}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ) : null}
                </>
            )}
        </div>
    );
}
